import { noop } from 'lodash';
import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { NormalizedUploadedFileData } from '@/modules/documents/documentUploaderUtils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { UploadedFileData } from '@/modules/files/FileUploaderV2/hooks/useUploadFile';
import { UploadFileV2Mutation } from '@/modules/files/graphql/FileUploaderV2.generated';
import { DocumentType } from '@/types/schema';

import type { UploadedFileEntityMapping } from '../types';

export interface SelectedExistingDocument {
  id: string;
  displayName: string;
}

export interface MultiDocumentUploaderContext {
  uploadedFiles: NormalizedUploadedFileData[];
  handleUploadFilesStarted: (files: File[]) => void;
  handleUploadFilesComplete: (
    uploaded: UploadedFileData<UploadFileV2Mutation>[]
  ) => void;
  handleDeleteFile: (fileId: string) => void;
  setDocumentTypeOfUploadedFile: (
    fileId: string,
    documentType: DocumentType
  ) => void;
  /**
   * Sends uploaded files within the context to the backend, and returns the document IDs.
   * This will throw on error, which should be handled by the caller.
   *
   * @param householdId
   * @param defaultDocumentType
   * @param entityId
   * @param fileIds Restrict creation to these uploaded-file IDs. Defaults to all
   *   uploaded files. Lets a caller persist just a subset (e.g. only the files
   *   not yet saved this session) so repeated calls don't duplicate Documents.
   */
  uploadDocuments: ({
    householdId,
    defaultDocumentType,
    entityId,
    entityType,
    fileIds,
  }: {
    householdId: string;
    defaultDocumentType: DocumentType;
    entityId?: string;
    entityType?: EntityType | undefined | null;
    fileIds?: string[];
  }) => Promise<string[]>;
  fileIDTypeMap: Record<string, DocumentType | ''>;
  clearUploadedFiles: () => void;
  uploaderErrorMessage: string | undefined;
  setUploaderErrorMessage: (errorMessage: string | undefined) => void;
  clearUploaderErrorMessage: () => void;

  // InferringDocumentTypes is true when there's an outstanding request to
  // infer the document types of the uploaded files.
  inferringDocumentTypes: boolean;

  // In some scenarios (new entity creation, AI onboarding), we want to allow the user
  // to select existing documents to operate on rather than (or in addition to!) uploading new ones.
  selectedExistingDocuments: SelectedExistingDocument[];
  setSelectedExistingDocuments: (documents: SelectedExistingDocument[]) => void;

  // Map of file IDs to entity mappings for per-file entity association
  uploadedFileEntityMap: Record<string, UploadedFileEntityMapping[]>;
  addEntityForUploadedFile: (
    fileId: string,
    mapping: UploadedFileEntityMapping
  ) => void;
  removeEntityForUploadedFile: (fileId: string, entityId: string) => void;
  setEntitiesForUploadedFile: (
    fileId: string,
    entities: UploadedFileEntityMapping[]
  ) => void;
  clearEntitiesForUploadedFile: (fileId: string) => void;
  isUploading: boolean;
  /**
   * Resolves once no raw file upload is in flight — immediately if nothing is
   * uploading. Lets a caller that must navigate away wait for in-flight uploads
   * to land (so their files can be turned into Documents) before leaving.
   */
  whenUploadsSettle: () => Promise<void>;
}

export const MultiDocumentUploaderContext =
  createContext<MultiDocumentUploaderContext>({
    uploadedFiles: [],
    handleUploadFilesStarted: noop,
    handleUploadFilesComplete: noop,
    handleDeleteFile: noop,
    setDocumentTypeOfUploadedFile: noop,
    uploadDocuments: () => Promise.resolve([]),
    fileIDTypeMap: {},
    clearUploadedFiles: noop,
    uploaderErrorMessage: undefined,
    setUploaderErrorMessage: noop,
    clearUploaderErrorMessage: noop,
    inferringDocumentTypes: false,
    selectedExistingDocuments: [],
    setSelectedExistingDocuments: noop,
    uploadedFileEntityMap: {},
    addEntityForUploadedFile: noop,
    removeEntityForUploadedFile: noop,
    setEntitiesForUploadedFile: noop,
    clearEntitiesForUploadedFile: noop,
    isUploading: false,
    whenUploadsSettle: () => Promise.resolve(),
  });

export const useMultiDocumentUploaderContext = () => {
  return useGuardedContext(
    MultiDocumentUploaderContext,
    'MultiDocumentUploaderContextProvider'
  );
};
